/**
 * FRONT-2382 — pure types + constants for the coding "New project" form's
 * "Design direction" section, behind `eddy.coding-design-direction`. Kept
 * free of React (same reasoning as `coding-events.ts`) so the payload
 * builder below is unit-testable without mounting anything, and both
 * `coding-session-modal.tsx`'s NewProjectView and `project-context-dialog.tsx`
 * import one shared vocabulary rather than two drifting copies.
 *
 * Mirrors the backend shapes verbatim (see the state file for the exact
 * routes/bodies): `src/db/schema/coding-design-briefs.ts` for
 * `DesignDirective`/`DesignReference`, `src/coding/routes.ts`'s
 * `designBriefView` for `DesignBriefView`, and `src/coding/design-suggestions.ts`
 * for `DesignSuggestion`.
 */

export type DesignDirective = 'colours' | 'typography' | 'layout' | 'follow-closely';

export const DESIGN_DIRECTIVES: readonly DesignDirective[] = [
  'colours',
  'typography',
  'layout',
  'follow-closely',
];

export const DESIGN_DIRECTIVE_LABELS: Record<DesignDirective, string> = {
  colours: 'Colours',
  typography: 'Typography',
  layout: 'Layout',
  'follow-closely': 'Follow closely',
};

/** "Suggest directions from the web" card — `POST /api/coding/design-suggestions`'s response shape. */
export interface DesignSuggestion {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
}

/**
 * One staged reference, as the server reports it back (`GET`/`PUT
 * /api/coding/sessions/:id/design`). `repoPath` is server-internal — the
 * client never needs it, so `DesignBriefView` below carries the field but
 * nothing in this file reads it.
 */
export interface DesignReference {
  id: string;
  kind: 'upload' | 'suggestion';
  title: string;
  description?: string;
  imageUrl?: string;
  fileId?: string;
  repoPath: string | null;
}

/** Client-facing brief shape — `designBriefView()`, `src/coding/routes.ts`. */
export interface DesignBriefView {
  inspiration: string;
  directives: DesignDirective[];
  references: DesignReference[];
  updatedAt: string;
}

/** A reference image already uploaded (via `useUploadFile`) and attached to the draft. */
export interface DesignUploadDraft {
  fileId: string;
  name: string;
  contentType: string;
}

/** A web suggestion the user picked, in the shape the create route wants for `design.picks`. */
export interface DesignPickDraft {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
}

/** The New-project form's own working state for the whole section. */
export interface DesignDirectionDraft {
  inspiration: string;
  directives: DesignDirective[];
  uploads: DesignUploadDraft[];
  picks: DesignPickDraft[];
}

export const EMPTY_DESIGN_DRAFT: DesignDirectionDraft = {
  inspiration: '',
  directives: [],
  uploads: [],
  picks: [],
};

/** Mirrors `MAX_DESIGN_INSPIRATION_CHARS`/`MAX_DESIGN_UPLOADS`/`MAX_DESIGN_PICKS` (`src/coding/design-references.ts`). */
export const MAX_DESIGN_INSPIRATION_CHARS = 2000;
export const MAX_DESIGN_UPLOADS = 5;
// Feedback round 2: lowered from 5 — 3 read as a real curated choice, 5 read
// as "just pick most of them". Uploads keep their own, unrelated cap.
export const MAX_DESIGN_PICKS = 3;

/** Mirrors the SDK's accepted image types (`SDK_IMAGE_MEDIA_TYPES`, `src/coding/uploads.ts`). */
export const DESIGN_REFERENCE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const DESIGN_REFERENCE_IMAGE_ACCEPT = DESIGN_REFERENCE_IMAGE_TYPES.join(',');

/**
 * FRONT-2382 review #4: pure application of a "Suggest directions from the
 * web" response onto the section's existing suggestions state. The route
 * can genuinely come back with zero verified cards (every candidate failed
 * to verify within its budget) — that must read as "try again" rather than
 * silently doing nothing while ALSO flipping the button to "More
 * suggestions" as if a batch had landed. Extracted as its own pure function
 * (rather than inlined in the mutation's `onSuccess`) so the empty-batch
 * case is provable without mounting `DesignDirectionDisclosure` and driving
 * its `useDesignSuggestions` mutation — this repo's frontend suite runs
 * under plain node with no DOM, so there is no way to click "Suggest
 * directions" and observe a state update.
 *
 * Feedback round 2: a new batch is PREPENDED, not appended — "More
 * suggestions" is meant to surface fresh ideas, and appending buried them
 * below everything already seen, so a person had to scroll past a growing
 * pile of old cards to find what just arrived.
 */
export function applySuggestionBatch({
  prevSuggestions,
  prevHasSuggested,
  batch,
}: {
  prevSuggestions: DesignSuggestion[];
  prevHasSuggested: boolean;
  batch: DesignSuggestion[];
}): { suggestions: DesignSuggestion[]; hasSuggested: boolean; error: string | null } {
  if (batch.length === 0) {
    return {
      suggestions: prevSuggestions,
      hasSuggested: prevHasSuggested,
      error: 'No new directions found — try again.',
    };
  }
  return { suggestions: [...batch, ...prevSuggestions], hasSuggested: true, error: null };
}

/** True when the draft has nothing worth sending — no text, no chip, no reference. Pure. */
export function isEmptyDesignDraft(draft: DesignDirectionDraft): boolean {
  return (
    draft.inspiration.trim() === '' &&
    draft.directives.length === 0 &&
    draft.uploads.length === 0 &&
    draft.picks.length === 0
  );
}

/** `POST /api/coding/sessions`'s optional `design` body field. */
export interface DesignCreatePayload {
  inspiration?: string;
  directives?: DesignDirective[];
  uploads?: { fileId: string }[];
  picks?: { title: string; description?: string; imageUrl: string }[];
}

/**
 * Whether — and what — to add to the create-session payload's `design`
 * field. `undefined` means "omit the field entirely", byte-identical to an
 * older client that never knew this section existed: the backend only
 * honours `body.design` when the flag is on (`src/coding/routes.ts`), and a
 * session with no design content gets no brief row at all
 * (`isEmptyDesignInput`, `src/coding/design-references.ts`) — so sending an
 * empty object would cost a round trip for nothing, and sending anything at
 * all while the flag is off would just be ignored server-side. Pure, so the
 * "flag off" and "nothing given" cases are each their own test rather than
 * something only provable by mounting the form.
 */
export function buildDesignCreatePayload(
  draft: DesignDirectionDraft,
  flagOn: boolean,
): DesignCreatePayload | undefined {
  if (!flagOn || isEmptyDesignDraft(draft)) return undefined;
  const payload: DesignCreatePayload = {};
  const inspiration = draft.inspiration.trim();
  if (inspiration) payload.inspiration = inspiration;
  if (draft.directives.length > 0) payload.directives = draft.directives;
  if (draft.uploads.length > 0) payload.uploads = draft.uploads.map((u) => ({ fileId: u.fileId }));
  if (draft.picks.length > 0) {
    payload.picks = draft.picks.map((p) => ({
      title: p.title,
      ...(p.description ? { description: p.description } : {}),
      imageUrl: p.imageUrl,
    }));
  }
  return payload;
}
